import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { format, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, subWeeks } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { parseDateSafe } from '@/lib/timezoneUtils';
import { Download, Filter, Calendar as CalendarIcon } from 'lucide-react';

import { FinancePageLayout, MoneyDisplay } from '@/components/finance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTransactionsReport } from '@/hooks/finance/useTransactionsReport';
import { cn } from '@/lib/utils';
import { PAYMENT_METHOD_LABEL, PAYMENT_METHOD_OPTIONS } from '@/lib/financeConstants';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'last_week', label: 'Semana passada' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'custom', label: 'Selecionar período' },
];

const PAYMENT_METHODS = [{ value: 'all', label: 'Todos' }, ...PAYMENT_METHOD_OPTIONS];

const STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'pending_confirmation', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'pending', label: 'Na fila' },
];

const TRIGGER_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'manual', label: 'Manual' },
  { value: 'automatic', label: 'Automático' },
];

const getDateRange = (period: string, customRange?: DateRange) => {
  const today = new Date();
  switch (period) {
    case 'today':
      return { start: startOfDay(today), end: endOfDay(today) };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    case 'this_week':
      return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfDay(today) };
    case 'last_week':
      const lastWeek = subWeeks(today, 1);
      return { 
        start: startOfWeek(lastWeek, { weekStartsOn: 1 }), 
        end: endOfWeek(lastWeek, { weekStartsOn: 1 }) 
      };
    case 'this_month':
      return { start: startOfMonth(today), end: endOfDay(today) };
    case 'last_month':
      const lastMonth = subMonths(today, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    case 'custom':
      return { 
        start: customRange?.from || subDays(today, 30), 
        end: customRange?.to || today 
      };
    default:
      return { start: subDays(today, 30), end: today };
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'confirmed':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Confirmado</Badge>;
    case 'rejected':
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Rejeitado</Badge>;
    case 'pending':
    case 'pending_confirmation':
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pendente</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const getMethodBadge = (method: string) => {
  const methodLabel = PAYMENT_METHOD_LABEL[method] || method;
  switch (method) {
    case 'stripe':
      return <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/20">{methodLabel}</Badge>;
    case 'pix':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">{methodLabel}</Badge>;
    case 'zelle':
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">{methodLabel}</Badge>;
    default:
      return <Badge variant="outline">{methodLabel}</Badge>;
  }
};

const getReconciliationBadge = (status: 'matched' | 'unmatched') => {
  if (status === 'matched') {
    return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Conciliado</Badge>;
  }
  return <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20">Pendente</Badge>;
};

const getVisibilityBadge = (hasLinkedPayment: boolean) => {
  if (hasLinkedPayment) {
    return <Badge className="bg-slate-500/10 text-slate-700 border-slate-400/20">Vinculada</Badge>;
  }
  return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Sem vínculo</Badge>;
};

const getVisibilityBadgeByStatus = (status: string, hasLinkedPayment: boolean) => {
  if (status === 'processing_failed') {
    return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Falha</Badge>;
  }
  if (status === 'awaiting_reconciliation') {
    return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Em análise</Badge>;
  }
  return getVisibilityBadge(hasLinkedPayment);
};

export default function TransactionsReport() {
  const [periodFilter, setPeriodFilter] = useState('this_month');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [status, setStatus] = useState('all');
  const [triggerType, setTriggerType] = useState('all');

  const dateRange = useMemo(() => getDateRange(periodFilter, customDateRange), [periodFilter, customDateRange]);

  const { data: transactions, isLoading } = useTransactionsReport({
    startDate: safeFormat(dateRange.start, 'yyyy-MM-dd'),
    endDate: safeFormat(dateRange.end, 'yyyy-MM-dd'),
    paymentMethod,
    status,
    triggerType,
  });

  const summary = useMemo(() => {
    if (!transactions) return { total: 0, confirmed: 0, pending: 0 };

    return {
      total: transactions.length,
      confirmed: transactions
        .filter(t => t.status === 'confirmed' || t.stripe_status === 'succeeded')
        .reduce((sum, t) => sum + t.amount, 0),
      pending: transactions
        .filter(t => t.status === 'pending_confirmation' || t.status === 'pending')
        .reduce((sum, t) => sum + t.amount, 0),
    };
  }, [transactions]);

  const handleExportCSV = () => {
    if (!transactions || transactions.length === 0) return;

    const headers = [
      'Data',
      'Cliente',
      'Deal',
      'Valor',
      'Moeda',
      'Método',
      'Status',
      'Conciliação',
      'Vínculo',
      'Origem',
      'Referência',
      'Stripe ID',
      'Registrado Por',
      'Notas',
    ];

    const rows = transactions.map(t => {
      const txDate = parseDateSafe(t.transaction_date);
      return [
      txDate ? safeFormat(txDate, 'dd/MM/yyyy') : '-',
      t.contact_name,
      t.deal_name,
      t.amount,
      t.currency,
      PAYMENT_METHOD_LABEL[t.payment_method] || t.payment_method,
      STATUSES.find(s => s.value === t.status)?.label || t.status,
      t.reconciliation_status === 'matched' ? 'Conciliado' : 'Pendente',
      t.payment_id ? 'Vinculada' : 'Sem vínculo',
      t.trigger_type || '-',
      t.reference_number || '-',
      t.stripe_payment_intent_id || t.stripe_charge_id || '-',
      t.recorded_by_name || '-',
      t.notes || '-',
    ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `extrato-transacoes-${safeFormat(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Extrato Financeiro</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Somente movimentos oficiais materializados no financeiro.
          </p>
        </div>
        <Button onClick={handleExportCSV} disabled={!transactions?.length}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Período */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                Período
              </Label>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Range personalizado - só aparece quando custom */}
            {periodFilter === 'custom' && (
              <div className="space-y-2">
                <Label>Intervalo</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn(
                      "w-full justify-start text-left font-normal",
                      !customDateRange?.from && "text-muted-foreground"
                    )}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange?.from ? (
                        customDateRange.to ? (
                          `${safeFormat(customDateRange.from, 'dd/MM/yyyy')} - ${safeFormat(customDateRange.to, 'dd/MM/yyyy')}`
                        ) : safeFormat(customDateRange.from, 'dd/MM/yyyy')
                      ) : 'Selecione as datas'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={setCustomDateRange}
                      numberOfMonths={2}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="space-y-2">
              <Label>Método</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Origem</Label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total de Lançamentos</CardDescription>
            <CardTitle className="text-2xl">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.total}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Valor Confirmado</CardDescription>
            <CardTitle className="text-2xl text-green-600">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <MoneyDisplay value={summary.confirmed} currency="USD" />
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Aguardando Confirmação</CardDescription>
            <CardTitle className="text-2xl text-yellow-600">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <MoneyDisplay value={summary.pending} currency="USD" />
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Deal</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Conciliação</TableHead>
                  <TableHead>Vínculo</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Stripe ID</TableHead>
                  <TableHead>Registrado Por</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 12 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : transactions?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      Nenhuma transação encontrada para os filtros selecionados
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions?.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>
                        {(() => {
                          const txDate = parseDateSafe(tx.transaction_date);
                          return txDate ? safeFormat(txDate, 'dd/MM/yyyy', { locale: ptBR }) : '-';
                        })()}
                      </TableCell>
                      <TableCell className="font-medium">{tx.contact_name}</TableCell>
                      <TableCell>{tx.deal_name}</TableCell>
                      <TableCell className="text-right font-mono">
                        <MoneyDisplay value={tx.amount} currency={tx.currency === 'BRL' ? 'BRL' : 'USD'} />
                      </TableCell>
                      <TableCell>{getMethodBadge(tx.payment_method)}</TableCell>
                      <TableCell>{getStatusBadge(tx.status)}</TableCell>
                      <TableCell>{getReconciliationBadge(tx.reconciliation_status)}</TableCell>
                      <TableCell>{getVisibilityBadgeByStatus(tx.visibility_status, Boolean(tx.payment_id))}</TableCell>
                      <TableCell>
                        {tx.trigger_type === 'automatic' ? (
                          <Badge variant="secondary">Automático</Badge>
                        ) : tx.trigger_type === 'manual' ? (
                          <Badge variant="outline">Manual</Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tx.reference_number || tx.stripe_charge_id || '-'}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs text-muted-foreground">
                        {tx.stripe_payment_intent_id || tx.stripe_charge_id || '-'}
                      </TableCell>
                      <TableCell>
                        {tx.recorded_by_name || (
                          <span className="text-muted-foreground italic">Sistema</span>
                        )}
                        {!tx.has_materialized_ledger && tx.review_reason ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {tx.review_reason}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
